/**
 * Страница экспорта данных
 * Современный тёмный дизайн
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import AnimatedCard from '../components/AnimatedCard';
import MagneticButton from '../components/MagneticButton';
import { showToast, initToast } from '../components/Toast';
import { API_URL } from '../utils/api.js';

export default function Export() {
  useEffect(() => {
    initToast();
  }, []);

  const [exportType, setExportType] = useState('excel');
  const [dataType, setDataType] = useState('users');
  const [loading, setLoading] = useState(false);
  const [webhookConfig, setWebhookConfig] = useState({
    url: '',
    method: 'POST',
  });
  const [airtableConfig, setAirtableConfig] = useState({
    apiKey: '',
    baseId: '',
    tableName: '',
  });

  const handleExcelExport = async () => {
    setLoading(true);
    try {
      const endpoint = dataType === 'users' ? '/users/export' : '/posts/export';
      const res = await axios.get(`${API_URL}${endpoint}`, {
        responseType: 'blob',
        params: { type: dataType },
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${dataType}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast('✅ Экспорт завершен!', 'success');
    } catch (error) {
      console.error('Ошибка при экспорте:', error);
      showToast('❌ Ошибка при экспорте: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleWebhookExport = async () => {
    if (!webhookConfig.url) {
      showToast('Введите URL webhook', 'warning');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/export/webhook`, {
        type: dataType,
        url: webhookConfig.url,
        method: webhookConfig.method,
      });
      showToast('✅ Данные отправлены на webhook!', 'success');
    } catch (error) {
      console.error('Ошибка при экспорте:', error);
      alert('❌ Ошибка: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAirtableExport = async () => {
    if (!airtableConfig.apiKey || !airtableConfig.baseId || !airtableConfig.tableName) {
      alert('Заполните все поля для Airtable');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/export/airtable`, {
        type: dataType,
        apiKey: airtableConfig.apiKey,
        baseId: airtableConfig.baseId,
        tableName: airtableConfig.tableName,
      });
      alert('✅ Данные экспортированы в Airtable!');
    } catch (error) {
      console.error('Ошибка при экспорте:', error);
      alert('❌ Ошибка: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const exportMethods = [
    { type: 'excel', label: 'Excel', icon: '📥', desc: 'Скачать файл Excel', gradient: 'from-green-500 to-emerald-500' },
    { type: 'webhook', label: 'Webhook', icon: '🔗', desc: 'Отправить на URL', gradient: 'from-blue-500 to-cyan-500' },
    { type: 'airtable', label: 'Airtable', icon: '📊', desc: 'Экспорт в Airtable', gradient: 'from-purple-500 to-pink-500' },
  ];

  return (
    <div className="min-h-screen bg-black p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 tracking-tighter">
            📤 Экспорт данных
          </h1>
          <p className="text-slate-400 tracking-tight">Выгрузка данных в различных форматах</p>
      </motion.div>

      {/* Выбор типа данных */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card"
      >
        <h2 className="text-xl font-bold text-white mb-4">📊 Тип данных</h2>
        <div className="flex flex-wrap gap-3">
          {[
            { type: 'users', label: '👥 Пользователи', icon: '👥' },
            { type: 'posts', label: '📝 Посты', icon: '📝' },
          ].map((item) => (
            <motion.button
              key={item.type}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setDataType(item.type)}
              className={`
                px-6 py-3 rounded-xl font-medium transition-all
                ${dataType === item.type
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/30'
                  : 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10'
                }
              `}
            >
              {item.label}
            </motion.button>
          ))}
        </div>
      </motion.div>

      {/* Выбор метода экспорта */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {exportMethods.map((method, i) => (
          <motion.div
            key={method.type}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            whileHover={{ scale: 1.02 }}
            onClick={() => setExportType(method.type)}
            className={`
              relative overflow-hidden rounded-2xl p-6 cursor-pointer transition-all
              ${exportType === method.type
                ? `bg-gradient-to-br ${method.gradient} shadow-lg`
                : 'bg-white/5 hover:bg-white/10 border border-white/10'
              }
            `}
          >
            <div className={`
              absolute top-0 right-0 w-20 h-20 rounded-full -translate-y-1/2 translate-x-1/2
              ${exportType === method.type ? 'bg-white/20' : 'bg-white/5'}
            `} />
            <div className="relative z-10">
              <span className="text-4xl mb-3 block">{method.icon}</span>
              <h3 className={`text-xl font-bold mb-1 ${exportType === method.type ? 'text-white' : 'text-gray-200'}`}>
                {method.label}
              </h3>
              <p className={exportType === method.type ? 'text-white/80' : 'text-gray-400'}>
                {method.desc}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Форма экспорта */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card"
      >
        {exportType === 'excel' && (
          <div>
            <h2 className="text-xl font-bold text-white mb-4">📥 Экспорт в Excel</h2>
            <p className="text-gray-400 mb-6">
              Экспортируйте {dataType === 'users' ? 'пользователей' : 'посты'} в файл Excel
            </p>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleExcelExport}
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-semibold shadow-lg shadow-green-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {loading ? '⏳ Экспорт...' : '📥 Скачать Excel'}
            </motion.button>
          </div>
        )}

        {exportType === 'webhook' && (
          <div>
            <h2 className="text-xl font-bold text-white mb-4">🔗 Экспорт через Webhook</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 text-sm mb-2">URL Webhook</label>
                <input
                  type="url"
                  value={webhookConfig.url}
                  onChange={(e) => setWebhookConfig({ ...webhookConfig, url: e.target.value })}
                  placeholder="https://example.com/webhook"
                  className="input"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">HTTP Метод</label>
                <select
                  value={webhookConfig.method}
                  onChange={(e) => setWebhookConfig({ ...webhookConfig, method: e.target.value })}
                  className="input"
                >
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="PATCH">PATCH</option>
                </select>
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleWebhookExport}
                disabled={loading || !webhookConfig.url}
                className="w-full py-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-semibold shadow-lg shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {loading ? '⏳ Отправка...' : '🚀 Отправить на Webhook'}
              </motion.button>
            </div>
          </div>
        )}

        {exportType === 'airtable' && (
          <div>
            <h2 className="text-xl font-bold text-white mb-4">📊 Экспорт в Airtable</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 text-sm mb-2">API Key</label>
                <input
                  type="password"
                  value={airtableConfig.apiKey}
                  onChange={(e) => setAirtableConfig({ ...airtableConfig, apiKey: e.target.value })}
                  placeholder="patXXXXXXXXXXXXXX"
                  className="input"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Base ID</label>
                <input
                  type="text"
                  value={airtableConfig.baseId}
                  onChange={(e) => setAirtableConfig({ ...airtableConfig, baseId: e.target.value })}
                  placeholder="appXXXXXXXXXXXXXX"
                  className="input"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Table Name</label>
                <input
                  type="text"
                  value={airtableConfig.tableName}
                  onChange={(e) => setAirtableConfig({ ...airtableConfig, tableName: e.target.value })}
                  placeholder="Table 1"
                  className="input"
                />
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleAirtableExport}
                disabled={loading || !airtableConfig.apiKey || !airtableConfig.baseId || !airtableConfig.tableName}
                className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold shadow-lg shadow-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {loading ? '⏳ Экспорт...' : '🚀 Экспортировать в Airtable'}
              </motion.button>
            </div>
          </div>
        )}
      </motion.div>
      </div>
    </div>
  );
}
